import React, { useEffect, useState } from "react";
import { db } from "../../../firebase";
 // adjust the path if your admin pages live elsewhere
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";

// Small helper to display label + value in a neat row
function InfoRow({ label, value }) {
  return (
    <div style={{ marginBottom: 4, display: "flex", gap: 8 }}>
      <div
        style={{
          minWidth: 160,
          fontSize: 12,
          color: "#6b7280",
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: "#111827" }}>
        {value || "—"}
      </div>
    </div>
  );
}

/**
 * Admin review for a single user's New Starter Payroll Form.
 *
 * Props:
 *   - userId: Firestore user doc id (same one you use for cvUpload / dbs etc).
 */
export default function NewStarterPayrollAdminReview({ userId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [docData, setDocData] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");

  useEffect(() => {
    if (!userId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const ref = doc(db, "users", userId, "complianceDocs", "newStarterPayroll");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setDocData({ id: snap.id, ...data });
          setAdminNotes(data.adminNotes || "");
        } else {
          setDocData(null);
        }
      } catch (err) {
        console.error("Error loading newStarterPayroll:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  const updateStatus = async (newStatus, newVerified) => {
    if (!userId || !docData) return;

    setSaving(true);
    try {
      const ref = doc(db, "users", userId, "complianceDocs", "newStarterPayroll");

      const updatePayload = {
        status: newStatus,
        verified: newVerified,
        adminNotes: adminNotes || "",
        lastUpdated: serverTimestamp(),
      };

      // Only stamp verifiedAt when we’re actually verifying
      if (newVerified) {
        updatePayload.verifiedAt = serverTimestamp();
      }

      await updateDoc(ref, updatePayload);

      setDocData((prev) => ({
        ...prev,
        ...updatePayload,
      }));

      alert(
        newVerified
          ? "Form marked as VERIFIED."
          : "Form marked as needing more information."
      );
    } catch (err) {
      console.error("Error updating newStarterPayroll:", err);
      alert("Failed to update payroll form. Check console for details.");
    } finally {
      setSaving(false);
    }
  };

  if (!userId) {
    return <div style={{ padding: 16 }}>Select a user to view their form.</div>;
  }

  if (loading) {
    return <div style={{ padding: 16 }}>Loading payroll form…</div>;
  }

  if (!docData) {
    return (
      <div style={{ padding: 16 }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
          New Starter Payroll Form
        </h3>
        <p style={{ fontSize: 13, color: "#6b7280" }}>
          No payroll form has been submitted by this user yet.
        </p>
      </div>
    );
  }

  const headerBg =
    docData.verified ? "#dcfce7" : docData.status === "needs-info" ? "#fee2e2" : "#e0f2fe";
  const headerColor =
    docData.verified ? "#166534" : docData.status === "needs-info" ? "#b91c1c" : "#075985";

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: "0 auto" }}>
      {/* Status banner */}
      <div
        style={{
          borderRadius: 12,
          padding: 12,
          marginBottom: 16,
          backgroundColor: headerBg,
          color: headerColor,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          border: "1px solid rgba(148,163,184,0.4)",
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            New Starter Payroll Form
          </div>
          <div style={{ fontSize: 13, marginTop: 2 }}>
            Status: <strong>{docData.status || "submitted"}</strong>{" "}
            {docData.verified && "• Verified"}
          </div>
        </div>
        <div style={{ fontSize: 12 }}>
          {docData.lastUpdated && (
            <div>
              Last updated:{" "}
              {docData.lastUpdated.toDate
                ? docData.lastUpdated.toDate().toLocaleString()
                : String(docData.lastUpdated)}
            </div>
          )}
          {docData.verifiedAt && (
            <div>
              Verified at:{" "}
              {docData.verifiedAt.toDate
                ? docData.verifiedAt.toDate().toLocaleString()
                : String(docData.verifiedAt)}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          disabled={saving}
          onClick={() => updateStatus("verified", true)}
          style={{
            padding: "8px 14px",
            borderRadius: 9999,
            border: "none",
            fontSize: 13,
            fontWeight: 600,
            backgroundColor: "#16a34a",
            color: "white",
            cursor: "pointer",
          }}
        >
          ✅ Mark as Verified
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => updateStatus("needs-info", false)}
          style={{
            padding: "8px 14px",
            borderRadius: 9999,
            border: "none",
            fontSize: 13,
            fontWeight: 600,
            backgroundColor: "#ef4444",
            color: "white",
            cursor: "pointer",
          }}
        >
          ⚠️ Needs More Information
        </button>
        {saving && (
          <span style={{ fontSize: 12, color: "#6b7280" }}>Saving…</span>
        )}
      </div>

      {/* Admin notes */}
      <div
        style={{
          marginBottom: 20,
          padding: 12,
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          backgroundColor: "#f9fafb",
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 6,
            color: "#111827",
          }}
        >
          Admin Notes (visible to admin; you can mirror to staff if you wish)
        </div>
        <textarea
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          rows={3}
          style={{
            width: "100%",
            borderRadius: 8,
            border: "1px solid #d1d5db",
            padding: 8,
            fontSize: 13,
            resize: "vertical",
          }}
        />
        <button
          type="button"
          disabled={saving}
          onClick={() =>
            updateStatus(docData.status || "submitted", !!docData.verified)
          }
          style={{
            marginTop: 8,
            padding: "6px 12px",
            borderRadius: 9999,
            border: "none",
            fontSize: 12,
            fontWeight: 600,
            backgroundColor: "#0ea5e9",
            color: "white",
            cursor: "pointer",
          }}
        >
          💾 Save Notes (keep current status)
        </button>
      </div>

      {/* Form details – read only, grouped like the staff form */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 12,
        }}
      >
        {/* 1. Personal Details */}
        <section
          style={{
            borderRadius: 12,
            padding: 12,
            border: "1px solid #e5e7eb",
            backgroundColor: "white",
          }}
        >
          <div
            style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
          >
            1. Personal Details
          </div>
          <InfoRow label="Full Name" value={docData.fullName} />
          <InfoRow label="Address Line 1" value={docData.addressLine1} />
          <InfoRow label="Address Line 2" value={docData.addressLine2} />
          <InfoRow label="Postcode" value={docData.postcode} />
          <InfoRow label="Date of Birth" value={docData.dob} />
          <InfoRow label="National Insurance Number" value={docData.niNumber} />
          <InfoRow label="Phone" value={docData.phone} />
          <InfoRow label="Email" value={docData.email} />
        </section>

        {/* 2. Starter Declaration */}
        <section
          style={{
            borderRadius: 12,
            padding: 12,
            border: "1px solid #e5e7eb",
            backgroundColor: "white",
          }}
        >
          <div
            style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
          >
            2. Starter Declaration (HMRC)
          </div>
          <InfoRow
            label="Starter Declaration"
            value={
              docData.starterDeclaration
                ? `Option ${docData.starterDeclaration}`
                : ""
            }
          />
        </section>

        {/* 3. Student Loans */}
        <section
          style={{
            borderRadius: 12,
            padding: 12,
            border: "1px solid #e5e7eb",
            backgroundColor: "white",
          }}
        >
          <div
            style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
          >
            3. Student Loans
          </div>
          <InfoRow
            label="Has Student Loan?"
            value={
              docData.hasStudentLoan === "yes"
                ? "Yes"
                : docData.hasStudentLoan === "no"
                ? "No"
                : ""
            }
          />
          {docData.hasStudentLoan === "yes" && (
            <>
              <InfoRow
                label="Student Loan Plan"
                value={docData.studentLoanPlan}
              />
              <InfoRow
                label="Postgraduate Loan?"
                value={
                  docData.hasPostgradLoan === "yes"
                    ? "Yes"
                    : docData.hasPostgradLoan === "no"
                    ? "No"
                    : ""
                }
              />
            </>
          )}
        </section>

        {/* 4. Right to Work */}
        <section
          style={{
            borderRadius: 12,
            padding: 12,
            border: "1px solid #e5e7eb",
            backgroundColor: "white",
          }}
        >
          <div
            style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
          >
            4. Right to Work in the UK
          </div>
          <InfoRow
            label="Share Code"
            value={docData.rightToWorkShareCode}
          />
        </section>

        {/* 5. Bank Details */}
        <section
          style={{
            borderRadius: 12,
            padding: 12,
            border: "1px solid #e5e7eb",
            backgroundColor: "white",
          }}
        >
          <div
            style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
          >
            5. Bank Details
          </div>
          <InfoRow
            label="Account Holder Name"
            value={docData.bankAccountName}
          />
          <InfoRow label="Bank Name" value={docData.bankName} />
          <InfoRow label="Sort Code" value={docData.sortCode} />
          <InfoRow
            label="Account Number"
            value={docData.accountNumber}
          />
          <InfoRow label="Roll Number" value={docData.rollNumber} />
        </section>

        {/* 6. Emergency Contact */}
        <section
          style={{
            borderRadius: 12,
            padding: 12,
            border: "1px solid #e5e7eb",
            backgroundColor: "white",
          }}
        >
          <div
            style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
          >
            6. Emergency Contact
          </div>
          <InfoRow
            label="Full Name"
            value={docData.emergencyContactName}
          />
          <InfoRow
            label="Relationship"
            value={docData.emergencyContactRelationship}
          />
          <InfoRow
            label="Phone Number"
            value={docData.emergencyContactPhone}
          />
        </section>

        {/* 7. Working Time & Other Employment */}
        <section
          style={{
            borderRadius: 12,
            padding: 12,
            border: "1px solid #e5e7eb",
            backgroundColor: "white",
          }}
        >
          <div
            style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
          >
            7. Working Time & Other Employment
          </div>
          <InfoRow
            label="Hours Restricted?"
            value={
              docData.hoursRestricted === "yes"
                ? "Yes"
                : docData.hoursRestricted === "no"
                ? "No"
                : ""
            }
          />
          {docData.hoursRestricted === "yes" && (
            <InfoRow
              label="Hours Restriction Details"
              value={docData.hoursDetails}
            />
          )}
          <InfoRow
            label="Other Employment?"
            value={
              docData.otherEmployment === "yes"
                ? "Yes"
                : docData.otherEmployment === "no"
                ? "No"
                : ""
            }
          />
          {docData.otherEmployment === "yes" && (
            <InfoRow
              label="Other Employment Details"
              value={docData.otherEmploymentDetails}
            />
          )}
        </section>

        {/* 8. Declaration */}
        <section
          style={{
            borderRadius: 12,
            padding: 12,
            border: "1px solid #e5e7eb",
            backgroundColor: "white",
          }}
        >
          <div
            style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
          >
            8. Declaration
          </div>
          <InfoRow
            label="Declaration Accepted"
            value={
              docData.declarationAccepted ? "Yes (box ticked)" : "No"
            }
          />
        </section>
      </div>
    </div>
  );
}
